import React from 'react';
import { Compass, ExternalLink, ShieldCheck, Sparkles } from 'lucide-react';
import type { AssistantSettingsSpecialistSnapshot } from './assistantSettingsSpecialist';

type AssistantSettingsSpecialistLeadProps = {
  snapshot: AssistantSettingsSpecialistSnapshot;
  onPresetCaseClick: (caseId: string) => void;
};

const metricToneClass: Record<string, string> = {
  info: 'border-primary/15 bg-primary/5 text-primary',
  success: 'border-jungle/15 bg-jungle/5 text-jungle',
  warning: 'border-amber-200 bg-amber-50 text-amber-800',
  neutral: 'border-slate-200 bg-white text-irongrey',
};

export const AssistantSettingsSpecialistLead: React.FC<AssistantSettingsSpecialistLeadProps> = ({
  snapshot,
  onPresetCaseClick,
}) => (
  <section
    className="rounded-[24px] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(244,249,248,0.96))] p-4 shadow-[0_18px_44px_rgba(36,50,53,0.06)]"
    data-testid="ai-settings-specialist-lead"
  >
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <ShieldCheck size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary/70">
          Settings specialist
        </p>
        <h3 className="mt-1 text-[15px] font-semibold leading-5 text-slate-900">
          {snapshot.title}
        </h3>
        <p className="mt-2 text-sm leading-6 text-irongrey/75">
          This route participates in a broader settings domain. Use these preset reviews to trace owner routes, downstream effects, and seeded readiness proof before making changes.
        </p>
      </div>
    </div>

    <div className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="ai-settings-specialist-metrics">
      {snapshot.metricItems.map((item, index) => (
        <article
          key={item.id}
          className={`rounded-[18px] border px-3 py-3 ${metricToneClass[item.tone ?? 'neutral'] ?? metricToneClass.neutral}`}
          data-testid={`ai-settings-specialist-metric-${index + 1}`}
        >
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em]">{item.label}</p>
          <p className="mt-1 text-lg font-black tracking-[-0.02em]">{item.value}</p>
          {item.detail ? (
            <p className="mt-1 text-[12px] leading-5 text-current/80">{item.detail}</p>
          ) : null}
        </article>
      ))}
    </div>

    {snapshot.ownerRoutes.length > 0 ? (
      <div className="mt-4" data-testid="ai-settings-specialist-owner-routes">
        <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-irongrey/55">
          <Compass size={12} />
          Owner routes
        </div>
        <div className="flex flex-wrap gap-2">
          {snapshot.ownerRoutes.map((route, index) => (
            <a
              key={route.id}
              href={route.href}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-irongrey shadow-[0_8px_18px_rgba(36,50,53,0.04)] transition-colors hover:border-primary/20 hover:bg-primary/5 hover:text-primary"
              data-testid={`ai-settings-specialist-owner-route-${index + 1}`}
              title={route.summary}
            >
              <span>{route.label}</span>
              <ExternalLink size={12} />
            </a>
          ))}
        </div>
      </div>
    ) : null}

    {snapshot.presetCases.length > 0 ? (
      <div className="mt-4" data-testid="ai-settings-specialist-cases">
        <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-irongrey/55">
          <Sparkles size={12} />
          Suggested reviews
        </div>
        <div className="flex flex-wrap gap-2">
          {snapshot.presetCases.map((entry, index) => (
            <button
              key={entry.id}
              type="button"
              className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-white px-3 py-1.5 text-[11px] font-semibold text-primary shadow-[0_8px_18px_rgba(36,50,53,0.04)] transition-colors hover:bg-primary hover:text-white"
              onClick={() => onPresetCaseClick(entry.id)}
              data-testid={`ai-settings-specialist-case-${index + 1}`}
              title={entry.summary}
            >
              <span>{entry.label}</span>
            </button>
          ))}
        </div>
      </div>
    ) : null}
  </section>
);
